import { useQuery } from 'react-query'
import { ServiceConfig } from '@user/store/services/config'
import { get } from '@user/store/services/crud'
import { ISearchResponse, searchInputToQueryString } from '@sales-center/common'
import { useTablePagination } from '@sales-center/components'
import { useDebounce } from '@user/utils/useDebounce'
import { MouseEvent, useEffect, useMemo } from 'react'
import { setPestPacLocationDuplicates } from '@user/store/duplicates/PestPacLocations/slice'
import { useAppDispatch } from '@user/store/hooks'

const baseUrl = `${ServiceConfig.baseAPIUrl}/location/search`

export interface ILocationSearchModel {
  id: string
  number: string
  billTo: LocationSearchBillTo
  searchTerm: string
  branchId: string
  branch: string | null
  billToId: string
  firstName: string
  lastName: string
  company: string
  phone: string
  phoneExt: string | null
  mobile: string
  email: string
  locationType: string | null
  taxCode: null
  terms: string | null
  division: string | null
  glCode: string | null
  address: ILocationSearchAddress
  campaign: string | null
  campaignId: string | null
  contactName: string
}

export type LocationSearchBillTo = {
  address: {
    address: string | null
    address2: string | null
    city: string | null
    country: string | null
    county: string | null
    latitude: number | null
    longitude: number | null
    postalCode: string | null
    state: string | null
  }
  altPhone: string | null
  altPhoneExt: string | null
  branch: string | null
  branchId: number | null
  company: string | null
  email: string | null
  firstName: string | null
  id: string | null
  lastName: string | null
  mobile: string | null
  number: string | null
  phone: string | null
  phoneExt: string | null
  billToTypeId: string | null
  divisionId: string | null
}

interface ILocationSearchAddress {
  address: string
  address2: string
  city: string
  state: string
  postalCode: string
  country: string | null
  county: string | null
  latitude: number
  longitude: number
}

export type LocationSearchResponse = {
  loading: boolean
  resultCount: number
  locations: ILocationSearchModel[]
  pageCount: number
  page: number
  perPage: number
  onChangePage: (event: MouseEvent<unknown> | null, newPage: number) => void
}

const fetchLocations = async (
  query: string | string[],
  page: number,
  perPage: number,
  branchId?: string,
): Promise<ISearchResponse<ILocationSearchModel>> => {
  const skip = page * perPage
  const fallback = { count: 0, items: [], skip: 0, take: 0 }
  if (typeof query === 'string') {
    if (query.trim() == '' || query.trim().length < 3) return fallback
  } else {
    query = query.filter((x) => x.length >= 3)
    if (query.length === 0) return fallback
  }

  const qstring = searchInputToQueryString({
    skip,
    take: perPage,
    query,
    branchIds: branchId,
  })
  const response = await get(`${baseUrl}?${qstring}`)
  if (response.ok && response.status !== 204) return await response.json()
  return { count: 0, items: [], skip: 0, take: 0 }
}

export const usePaginatedLocationSearch = (
  query: string | string[],
  perPage: number = 10,
  debounceDelay: number = 1000,
  page: number = 1,
): LocationSearchResponse => {
  const dispatch = useAppDispatch()
  const debounced = useDebounce(query, debounceDelay)
  const { onChangePage } = useTablePagination()

  const { isFetching: loading, data } = useQuery(
    ['duplicateCheck', debounced, page, perPage],
    () => fetchLocations(debounced, page - 1, perPage),
    { keepPreviousData: true },
  )

  const resultCount = data?.count ?? 0

  const locations = data?.items ?? []

  const pageCount = useMemo(
    () => Math.ceil(resultCount / perPage),
    [resultCount, perPage],
  )

  useEffect(() => {
    if (data) {
      dispatch(
        setPestPacLocationDuplicates({
          resultCount,
          duplicates: locations,
          pageCount,
          page,
          perPage,
        }),
      )
    }
  }, [data, dispatch, locations, page, pageCount, perPage, resultCount])

  return {
    loading,
    resultCount,
    locations,
    pageCount,
    page,
    perPage,
    onChangePage,
  }
}
